import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

export type UsePolling = {
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  shouldPoll: boolean;
  interval?: number;
  timeout?: number;
  onStop?: () => void;
};

const DEFAULT_INTERVAL = 1000;
const DEFAULT_TIMEOUT = 300000; // 5 mins

export const usePolling = ({
  startPolling,
  stopPolling,
  shouldPoll,
  interval = DEFAULT_INTERVAL,
  timeout = DEFAULT_TIMEOUT,
  onStop,
}: UsePolling): {
  isPolling: boolean;
} => {
  const [isPolling, setIsPolling] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasTimedOut(true);
    }, timeout);

    if (!isPolling && shouldPoll && !hasTimedOut) {
      startPolling(interval);
      return setIsPolling(true);
    }

    if (isPolling && !shouldPoll) {
      stopPolling();
      if (onStop) onStop();
      return setIsPolling(false);
    }

    if (isPolling && hasTimedOut) {
      Sentry.captureMessage(`Polling exceeded the timeout of ${timeout}ms`, {
        level: 'warning',
      });
      stopPolling();
      return setIsPolling(false);
    }

    return () => clearTimeout(timer);
  }, [
    hasTimedOut,
    interval,
    isPolling,
    onStop,
    shouldPoll,
    startPolling,
    stopPolling,
    timeout,
  ]);

  return {
    isPolling,
  };
};
